import { LitElement, html, unsafeCSS } from "lit"
import { customElement, property, state } from "lit/decorators.js"
import { unsafeHTML } from "lit/directives/unsafe-html.js"
import { marked } from "marked"
import styles from "./styles/help-viewer.css?inline"
import { APP_VERSION } from "../firebase"
import type { MiniRouter } from "../router"

@customElement("help-viewer")
export class HelpViewer extends LitElement {
  @property({ type: String }) page = "home"
  @property({ type: Object }) router?: MiniRouter
  @state() private content = ""
  @state() private isLoading = false
  @state() private error: string | null = null

  static styles = unsafeCSS(styles)

  async connectedCallback() {
    super.connectedCallback()
    await this.loadContent()
  }

  async updated(changedProperties: Map<string, unknown>) {
    if (changedProperties.has("page")) {
      await this.loadContent()
    }
    
    // Transform internal help links to use router navigation
    if (changedProperties.has("content") && this.content) {
      this.transformHelpLinks()
    }
  }

  private transformHelpLinks() {
    // Wait for the next render cycle to ensure DOM is updated
    requestAnimationFrame(() => {
      const links = this.shadowRoot?.querySelectorAll("a")
      
      links?.forEach(link => {
        const href = link.getAttribute("href")
        
        if (href && href.startsWith("/help") && this.router) {
          // Internal help link - use router navigation
          link.addEventListener("click", (event: Event) => {
            event.preventDefault()
            this.router?.navigate(href)
          })
          
          // Add a class for styling if needed
          link.classList.add("internal-link")
        } else if (href && href.startsWith("http")) {
          // External link - ensure it opens in new tab
          link.setAttribute("target", "_blank")
          link.setAttribute("rel", "noopener noreferrer")
        }
      })
    })
  }

  private async loadContent() {
    this.isLoading = true
    this.error = null

    try {
      // The page property represents the path within /help/
      // Empty or missing defaults to "home"
      // Directory paths (like "slooh") should load that directory's home.md
      // Specific files (like "slooh/capturing-live") load directly
      
      let pagePath = this.page || "home"
      
      // If the path doesn't contain a known page name, append /home
      // This handles: "slooh" -> "slooh/home", but leaves "slooh/capturing-live" as-is
      const pathParts = pagePath.split("/")
      const lastPart = pathParts[pathParts.length - 1]
      
      // If last part looks like a directory (single word without known page indicators),
      // and it's not already "home", append /home
      if (lastPart && lastPart !== "home" && !lastPart.includes("-")) {
        pagePath = `${pagePath}/home`
      }
      
      const url = `/help/${pagePath}.md`
      const response = await fetch(url)

      if (!response.ok) {
        if (response.status === 404) {
          throw new Error(`Help page "${pagePath}" not found.`)
        }
        throw new Error(`Failed to load help page: ${response.statusText}`)
      }

      const markdown = await response.text()
      
      // Check if we accidentally got HTML instead of markdown
      if (markdown.trim().startsWith('<!DOCTYPE') || markdown.trim().startsWith('<html')) {
        throw new Error(`Received HTML instead of markdown for ${url}`)
      }
      
      this.content = marked.parse(markdown) as string
    } catch (err) {
      this.error =
        err instanceof Error ? err.message : "An unknown error occurred"
      console.error("Failed to load help content:", err)
    } finally {
      this.isLoading = false
    }
  }

  protected render() {
    if (this.isLoading) {
      return html`<div class="loading">Loading help content...</div>`
    }

    if (this.error) {
      return html`<div class="error">${this.error}</div>`
    }

    return html`
      <div class="container">
        <div class="markdown-content">${unsafeHTML(this.content)}</div>
        <div class="copyright">
          ©${new Date().getFullYear()}
          <a
            href="https://davidbethune.com"
            target="_blank"
            rel="noopener noreferrer"
            >David Bethune</a
          >. Your feedback and ideas are welcome! v${APP_VERSION}
        </div>
      </div>
    `
  }
}
